import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useGetData } from "../../../../../hooks";
import { EndPoints } from "../../../../../Constants/endpoints";
import { MealType } from "../../../../../interface/GeneralResponse";
import { OptionType } from "../../../../../interface/OptionTypes";
import { MedicalKeys } from "../../../../../enums/MedicalReportEnums";
import GeneralSelectWithInput from "../GeneralSelectWithInput/GeneralSelectWithInput";
import MedicalRichTextEditor from "../MedicalRichTextEditor/MedicalRichTextEditor";
import RemoveIcon from "../../../../../assets/images/x-02.svg";
import {
  GeneralMessage,
  GeneralRadio,
  GeneralSelect,
  GeneralSelectApi,
  UnitInput,
} from "../../../../../components";
import { useEffect } from "react";

const Prescription = ({
  name,
  fieldsConfig,
  disabled,
}: {
  name: string;
  fieldsConfig: any;
  disabled?: boolean;
}) => {
  const { t } = useTranslation();
  const { control, watch, getValues, setValue } = useFormContext();

  const isTreatment = fieldsConfig?.Key === MedicalKeys.Treatments;
  const drugsName = isTreatment ? `${name}.Drugs` : name;
  const optionsName = `${drugsName}-option`;

  const fieldArray = useFieldArray({ control, name: drugsName });

  const { fields, replace, remove } = fieldArray;

  const pregnancyDate = watch(fieldsConfig)?.DateOfPregnancy;
  const week = pregnancyDate
    ? Math.abs(moment().diff(moment(pregnancyDate, "DD-MM-YYYY"), "weeks")) + 1
    : 0;

  const { data: mealTypesResponse } = useGetData<MealType[]>(
    EndPoints.GetMealTypes,
  );

  const { data: FrequencyTypes } = useGetData<any[]>(EndPoints.FrequencyTypes);

  const frequencyTypesOptions: OptionType[] =
    FrequencyTypes?.Data?.map((m) => ({
      key: m.Code,
      value: m.Code,
      label: m.Name,
    })) ?? [];

  const mealTypeOptions: OptionType[] =
    mealTypesResponse?.Data?.map((m) => ({
      key: m.Code,
      value: m.Code,
      label: m.Name,
    })) ?? [];

  const timesOptions: OptionType[] = Array.from({ length: 10 }, (_, i) => {
    const v = String(i + 1);
    return { key: v, value: v, label: v };
  });

  const handleTemplateChange = (opt: any) => {
    setValue(`${drugsName}.Case`, moment().format("DD-MM-YYYY"));

    if (!opt?.value) {
      replace([]);
      setValue("PrescriptionTemplatesId", null);
      setValue(optionsName, []);
      return;
    }

    setValue("PrescriptionTemplatesId", opt.value);

    const drugs = opt?.Drugs ?? [];

    replace(
      drugs.map((d: any, index: number) => ({
        key: d?.IsExternal ? d?.Name : (d.DosageFormName ?? d.DosageForm),
        value: d?.IsExternal ? d?.Name : d.PackageId,
        label: d.PackageName || d.Name,
        ...d,
        Name: d.PackageName || d.Name || d.DrugName,
        Meal: d?.MealCode,
        Frequency: d?.FrequencyCode,
      })),
    );

    setValue(
      optionsName,
      drugs.map((d: any, index: number) => ({
        key: d?.IsExternal ? d?.Name : (d.DosageFormName ?? d.DosageForm),
        value: d?.IsExternal ? d?.Name : d.PackageId,
        label: d.PackageName || d.Name,
        ...d,
        Name: d.PackageName || d.Name || d.DrugName,
        Meal: d?.MealCode,
        Frequency: d?.FrequencyCode,
      })),
    );
  };


  const drugsValue = getValues(drugsName);
  useEffect(() => {


    if (!drugsValue?.length) {
      setValue(drugsName, undefined)
    }

  }, [drugsValue])

  return (
    <div className="flex gap-2">
      <div className="flex flex-1 flex-col gap-4">
        {isTreatment && (
          <>
            <MedicalRichTextEditor
              field={{
                Key: `${MedicalKeys.Treatments}.TreatmentPlan`,
                Name: t("treatment_plan"),
              }}
              disabled={disabled}
            />

            <GeneralSelectApi
              isDisabled={disabled}
              endpoint={EndPoints.GetPrescriptions}
              name="PrescriptionTemplatesId"
              itemsPath="Data.Prescriptions"
              labelKey="Case"
              placeholder={t("prescription_templates")}
              onChange={handleTemplateChange}
              customControlStyles={{
                background: "white !important",
                minHeight: "50px !important",
                border: "1px solid #566B8D !important",
                borderRadius: "8px",
              }}
            />
          </>
        )}

        <GeneralSelectWithInput
          isMulti
          endpoint={EndPoints.SearchDrug}
          name={drugsName}
          labelKey="PackageName"
          valueKey="PackageId"
          keyKey="DosageFormName"
          placeholder={t("select_medications")}
          fieldArray={fieldArray}
          fieldsConfig={fieldsConfig}
          isClearable={false}
          isDisabled={disabled}
        />

        {fields?.map((drug: any, index: number) => {
          const base = `${drugsName}.${index}`;

          return (
            <div
              key={drug.id}
              className="bg-primary-bg flex items-start gap-3 rounded-lg p-4"
            >
              <div className="flex w-56 items-center gap-3">
                <img
                  alt=""
                  src={RemoveIcon}
                  className="h-5 w-5 cursor-pointer"
                  onClick={() => {
                    remove(index);
                    const opts = getValues(optionsName) ?? [];

                    setValue(
                      optionsName,
                      opts.filter((o: any) => {
                        if (o?.IsExternal) return o.value !== drug.Name;
                        else return o.value !== (drug.Id ?? drug?.PackageId);
                      }),
                    );
                  }}
                />
                <p className="">{drug.Name ?? drug?.PackageName}</p>
              </div>

              <div className="flex flex-1 flex-col gap-5">
                <div className="grid grid-cols-3 gap-3">
                  <UnitInput
                    label={t("dose_amount_per_time")}
                    name={`${base}.DosageNum`}
                    type={
                      drug?.DosageFormUnitName ??
                      drug.DosageFormName ??
                      drug?.DosageForm
                    }
                    numberOnly
                    customStyleClasses="!h-[50px] !border !border-body-text !bg-white placeholder:!text-text-muted"
                  />
                  <GeneralSelect
                    name={`${base}.Times`}
                    options={timesOptions}
                    placeholder={t("how_many_times")}
                    customControlStyles={{
                      background: "white !important",
                      minHeight: "50px !important",
                      border: "1px solid #566B8D !important",
                      borderRadius: "8px",
                    }}
                  />
                  <UnitInput
                    label={t("duration")}
                    placeholder={t("duration")}
                    type={t("days")}
                    name={`${base}.Duration`}
                    numberOnly
                    customStyleClasses="w-full !h-[50px] !border !border-body-text !bg-white placeholder:!text-text-muted"
                  />
                </div>

                <div className="flex w-full flex-col gap-4">
                  <div className="flex justify-between">
                    <Controller
                      name={`${base}.Frequency`}
                      control={control}
                      render={({ field }) => (
                        <div className="flex flex-col gap-2">
                          <h2>{t("frequency")}</h2>
                          <div className="flex flex-1 flex-wrap gap-3">
                            {frequencyTypesOptions.map((o) => (
                              <GeneralRadio
                                key={o.key}
                                {...field}
                                value={o.value}
                                label={o.label}
                                checked={field.value === o.value}
                                onClick={() => field.onChange(o.value)}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    />

                    <Controller
                      name={`${base}.Meal`}
                      control={control}
                      render={({ field }) => (
                        <div className="flex flex-col gap-2">
                          <h2>{t("period")}</h2>
                          <div className="flex flex-1 flex-wrap gap-3">
                            {mealTypeOptions.map((o) => (
                              <GeneralRadio
                                key={o.key}
                                {...field}
                                value={o.value}
                                label={o.label}
                                checked={field.value === o.value}
                                onClick={() => field.onChange(o.value)}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    />
                    <div className="flex flex-col gap-4">
                      <Controller
                        control={control}
                        name={`${base}.Sleep`}
                        render={({ field }) => (
                          <div className="flex flex-col gap-2">
                            <h2>{t("sleep")}</h2>
                            <GeneralRadio
                              name={field.name}
                              label={t("before_sleep")}
                              value="true"
                              checked={!!field.value}
                              onClick={() => field.onChange(!field.value)}
                            />
                          </div>
                        )}
                      />
                    </div>
                  </div>

                  <Controller
                    name={`${base}.Instructions`}
                    control={control}
                    render={({ field }) => (
                      <textarea
                        {...field}
                        value={field.value || ""}
                        // type="textarea"
                        placeholder={t("instructions")}
                        className="!outline-body-text h-32 w-full rounded-lg p-3 outline-none outline-1 focus:outline-2 focus:!outline-primary"
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {watch(fieldsConfig)?.IsPregnancy === "true" && isTreatment && (
        <GeneralMessage
          status="error"
          className="flex !max-h-40 max-w-[18%] flex-col items-center justify-center text-center"
          message={t("patient_pregnant_at_week", { week })}
        />
      )}
    </div>
  );
};

export default Prescription;
